import type { ActivityStatus } from 'api/recoveryAi.types'
import type { SurfaceVariant } from 'components/orderFeedbackSurface'
import { buildTranslate } from 'locales'
import React from 'react'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'

const t = buildTranslate('components.feedback_status_badge')

const STATUS_COLORS: Record<ActivityStatus, { bg: string; text: string }> = {
  resolved: {
    bg: merchantTheme.colors.nephritis10,
    text: merchantTheme.colors.nephritis50,
  },
  manually_resolved: {
    bg: merchantTheme.colors.nephritis10,
    text: merchantTheme.colors.nephritis50,
  },
  unresolved: {
    bg: merchantTheme.colors.cayenne10,
    text: merchantTheme.colors.cayenne50,
  },
  escalated: {
    bg: merchantTheme.colors.eggYolk10,
    text: merchantTheme.colors.eggYolk50,
  },
  pending: {
    bg: merchantTheme.colors.grey20,
    text: merchantTheme.colors.grey70,
  },
  // Neutral, not "resolved" green — the wire can't tell an operator's
  // dismissal apart from a manual resolution, so this reads as closed rather
  // than asserting an outcome. Same grey treatment as pending.
  closed_by_operator: {
    bg: merchantTheme.colors.grey20,
    text: merchantTheme.colors.grey70,
  },
}

// Per-surface text treatment. The RAI case detail (default) renders the pill
// semibold and sentence-case; the order-rating detail redesign renders it
// non-bold and uppercase (via CSS, so the accessible text stays sentence-case).
const TEXT_BY_VARIANT: Record<
  SurfaceVariant,
  { fontWeight: 400 | 600; textTransform: 'none' | 'uppercase' }
> = {
  'case-detail': { fontWeight: 600, textTransform: 'none' },
  'order-rating': { fontWeight: 400, textTransform: 'uppercase' },
}

type Props = {
  status: ActivityStatus
  // Which surface this badge renders on. Defaults to the RAI case detail's
  // semibold, sentence-case look, unchanged for every existing caller.
  variant?: SurfaceVariant
}

const FeedbackStatusBadge = ({ status, variant = 'case-detail' }: Props) => {
  const [css] = useStyletron()
  const colors = STATUS_COLORS[status] || STATUS_COLORS.pending
  const { fontWeight, textTransform } = TEXT_BY_VARIANT[variant]

  return (
    <span
      className={css({
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight,
        textTransform,
        backgroundColor: colors.bg,
        color: colors.text,
      })}
    >
      {t(status)}
    </span>
  )
}

export default FeedbackStatusBadge
